/* 💎 Phoenix Jewels — holiday-only classic match-3 (Bejeweled with a Phoenix twist)
 * 60s runs · swap adjacent gems · 3+ in a row clears · cascades multiply.
 * Cumulative event points unlock Level Boxes 📦 (server-authoritative claims).
 */
import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZE = 8;
const GEMS = ["🔥", "💎", "🪶", "🌟", "🥚", "🌋"];
const RUN_SECONDS = 60;

// ─── board helpers ───
const idx = (r, c) => r * SIZE + c;
const randGem = () => GEMS[Math.floor(Math.random() * GEMS.length)];

function genBoard() {
  const b = new Array(SIZE * SIZE).fill(null);
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      let g;
      do {
        g = randGem();
      } while (
        (c >= 2 && b[idx(r, c - 1)] === g && b[idx(r, c - 2)] === g) ||
        (r >= 2 && b[idx(r - 1, c)] === g && b[idx(r - 2, c)] === g)
      );
      b[idx(r, c)] = g;
    }
  }
  return b;
}

function findMatches(b) {
  const hit = new Set();
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE - 2; c++) {
      const g = b[idx(r, c)];
      if (g && g === b[idx(r, c + 1)] && g === b[idx(r, c + 2)]) {
        let e = c + 2;
        while (e + 1 < SIZE && b[idx(r, e + 1)] === g) e++;
        for (let k = c; k <= e; k++) hit.add(idx(r, k));
      }
    }
  }
  for (let c = 0; c < SIZE; c++) {
    for (let r = 0; r < SIZE - 2; r++) {
      const g = b[idx(r, c)];
      if (g && g === b[idx(r + 1, c)] && g === b[idx(r + 2, c)]) {
        let e = r + 2;
        while (e + 1 < SIZE && b[idx(e + 1, c)] === g) e++;
        for (let k = r; k <= e; k++) hit.add(idx(k, c));
      }
    }
  }
  return hit;
}

function collapse(b) {
  const nb = [...b];
  for (let c = 0; c < SIZE; c++) {
    let write = SIZE - 1;
    for (let r = SIZE - 1; r >= 0; r--) {
      if (nb[idx(r, c)] !== null) {
        nb[idx(write, c)] = nb[idx(r, c)];
        if (write !== r) nb[idx(r, c)] = null;
        write--;
      }
    }
    for (let r = write; r >= 0; r--) nb[idx(r, c)] = randGem();
  }
  return nb;
}

export default function JewelsGame() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [clearing, setClearing] = useState(new Set());
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(RUN_SECONDS);
  const [running, setRunning] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const scoreRef = useRef(0);
  const runningRef = useRef(false);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/arcade/jewels/state`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Phoenix Jewels.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchState(); }, [fetchState]);

  // ─── run timer ───
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(id); endRun(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const startRun = () => {
    setBoard(genBoard());
    setScore(0);
    scoreRef.current = 0;
    setTimeLeft(RUN_SECONDS);
    setSelected(null);
    setRunning(true);
    runningRef.current = true;
  };

  const endRun = async () => {
    setRunning(false);
    runningRef.current = false;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/arcade/jewels/complete`, { score: scoreRef.current });
      toast.success(`💎 Run banked: +${data.run_score} pts → ${data.score.toLocaleString()} event points!`);
      setState((s) => s ? { ...s, score: data.score, runs_today: data.runs_today, boxes: data.boxes } : s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit run.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── cascade resolution ───
  const resolveMatches = useCallback((startBoard) => {
    setResolving(true);
    let combo = 0;
    const step = (b) => {
      const matches = findMatches(b);
      if (matches.size === 0) {
        setClearing(new Set());
        setResolving(false);
        return;
      }
      combo += 1;
      const gained = matches.size * 10 * combo;
      if (runningRef.current) {
        scoreRef.current += gained;
        setScore(scoreRef.current);
      }
      setClearing(new Set(matches));
      setTimeout(() => {
        const nb = [...b];
        matches.forEach((i) => { nb[i] = null; });
        const filled = collapse(nb);
        setClearing(new Set());
        setBoard(filled);
        setTimeout(() => step(filled), 180);
      }, 260);
    };
    step(startBoard);
  }, []);

  const onCellClick = (r, c) => {
    if (!running || resolving) return;
    const i = idx(r, c);
    if (selected === null) { setSelected(i); return; }
    if (selected === i) { setSelected(null); return; }
    const sr = Math.floor(selected / SIZE), sc = selected % SIZE;
    const adjacent = Math.abs(sr - r) + Math.abs(sc - c) === 1;
    if (!adjacent) { setSelected(i); return; }
    const nb = [...board];
    [nb[selected], nb[i]] = [nb[i], nb[selected]];
    setSelected(null);
    if (findMatches(nb).size === 0) {
      // invalid swap — flash revert
      setBoard(nb);
      setTimeout(() => setBoard(board), 200);
      return;
    }
    setBoard(nb);
    resolveMatches(nb);
  };

  const claimBox = async (b) => {
    setClaiming(b.index);
    try {
      const { data } = await axios.post(`${API}/arcade/jewels/claim-box`, { box_index: b.index });
      const spinNote = data.extra_spins ? ` · +${data.extra_spins} 🎰 Extra Spin${data.extra_spins === 1 ? "" : "s"}` : "";
      toast.success(`${b.icon} Box ${b.index} opened → +${data.reward_pt} Phoenix Tokens${spinNote}! Balance: ${data.balance}`);
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim box.");
    } finally {
      setClaiming(null);
    }
  };

  const toggleEvent = async (active) => {
    try {
      await axios.post(`${API}/arcade/jewels/admin/set`, { active, label: "Special Event", days: 3 });
      toast.success(active ? "💎 Special Jewels event started (3 days)!" : "Event ended.");
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    }
  };

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="animate-spin inline text-[#D4AF37]" /></div>;
  }

  // ─── locked (no holiday / event) ───
  if (!state?.active) {
    return (
      <div data-testid="arcade-jewels-locked" className="text-center py-10">
        <div className="text-6xl mb-4">💎</div>
        <h3 className="font-display text-3xl text-white mb-2">Phoenix Jewels</h3>
        <p className="text-neutral-400 text-sm max-w-md mx-auto">
          {state?.message || "Returns during holidays & special events."}
        </p>
        <div className="mt-6 flex justify-center gap-2 flex-wrap">
          {(state?.boxes || []).map((b) => (
            <div key={b.index} className="w-14 h-14 border border-zinc-800 bg-black/40 flex flex-col items-center justify-center opacity-50">
              <span className="text-xl">{b.icon}</span>
              <span className="text-[8px] text-neutral-500">{b.threshold.toLocaleString()}</span>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-neutral-600 mt-4 uppercase tracking-widest">
          🎄 New Year · Valentine&apos;s · St. Patrick&apos;s · July 4th · Halloween · Thanksgiving · Christmas
        </p>
        {/* 🪦 Feb 2026 owner directive — removed "Owner: Start Special Event"
           button. Arcade games (Jewel Hunt) are event-only; they must NOT
           be selectable/toggleable by the admin outside holiday windows.
           Date-driven backend logic still activates during real events. */}
      </div>
    );
  }

  const boxes = state.boxes || [];
  return (
    <div data-testid="arcade-jewels" className="text-center">
      <div className="flex items-center justify-center gap-3 mb-1 flex-wrap">
        <h3 className="font-display text-3xl text-white">💎 Phoenix Jewels</h3>
        <span className="px-3 py-1 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] font-black uppercase tracking-widest">
          {state.label}
        </span>
      </div>
      <p className="text-neutral-400 text-xs mb-4">
        Swap adjacent gems · 3+ in a row clears · cascades multiply · 60s runs ·
        {" "}{state.runs_today}/{state.runs_cap} runs today
      </p>

      {/* 📦 Level Boxes */}
      <div className="flex justify-center gap-2 mb-5 flex-wrap" data-testid="jewels-boxes">
        {boxes.map((b) => (
          <button
            key={b.index}
            data-testid={`jewels-box-${b.index}`}
            disabled={!b.unlocked || b.claimed || claiming === b.index}
            onClick={() => claimBox(b)}
            className={`w-20 py-2 border-2 flex flex-col items-center transition-all ${
              b.claimed
                ? "border-emerald-700 bg-emerald-950/30 opacity-60"
                : b.unlocked
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 animate-pulse cursor-pointer hover:scale-105"
                  : "border-zinc-800 bg-black/40 opacity-50 cursor-not-allowed"
            }`}
          >
            <span className="text-2xl">{b.claimed ? "✅" : b.icon}</span>
            <span className="text-[9px] font-bold text-neutral-300">
              {b.reward_pt} Phoenix Tokens{b.extra_spins ? ` · 🎰${b.extra_spins}` : ""}
            </span>
            <span className="text-[8px] text-neutral-500">{b.threshold.toLocaleString()} pts</span>
          </button>
        ))}
      </div>

      {/* score strip */}
      <div className="flex justify-center gap-6 mb-4 text-sm">
        <div><span className="text-neutral-500 text-[10px] uppercase tracking-widest block">Event Points</span>
          <span className="font-black text-[#D4AF37] text-xl" data-testid="jewels-event-score">{(state.score || 0).toLocaleString()}</span></div>
        {running && (
          <>
            <div><span className="text-neutral-500 text-[10px] uppercase tracking-widest block">Run Score</span>
              <span className="font-black text-white text-xl" data-testid="jewels-run-score">{score}</span></div>
            <div><span className="text-neutral-500 text-[10px] uppercase tracking-widest block">Time</span>
              <span className={`font-black text-xl ${timeLeft <= 10 ? "text-rose-400" : "text-emerald-300"}`}>{timeLeft}s</span></div>
          </>
        )}
      </div>

      {/* board */}
      {board && (running || resolving) ? (
        <div className="inline-grid gap-1 p-2 bg-black/60 border border-zinc-800"
             style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0,1fr))` }}
             data-testid="jewels-board">
          {board.map((g, i) => {
            const r = Math.floor(i / SIZE), c = i % SIZE;
            const isSel = selected === i;
            const isClr = clearing.has(i);
            return (
              <button
                key={i}
                data-testid={`jewel-${r}-${c}`}
                onClick={() => onCellClick(r, c)}
                className={`w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center text-xl sm:text-2xl border transition-all duration-150 ${
                  isClr ? "scale-0 opacity-0 border-transparent"
                  : isSel ? "border-[#D4AF37] bg-[#D4AF37]/25 scale-110"
                  : "border-zinc-800/60 bg-zinc-900/40 hover:bg-zinc-800/60"
                }`}
              >
                {g}
              </button>
            );
          })}
        </div>
      ) : (
        <button
          data-testid="jewels-start"
          onClick={startRun}
          disabled={submitting || state.runs_today >= state.runs_cap}
          className="px-8 py-4 bg-[#D4AF37] text-black font-black uppercase tracking-widest text-sm hover:bg-[#c19d2e] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? "Banking run…"
            : state.runs_today >= state.runs_cap ? "Daily cap reached — back tomorrow"
            : "🐦‍🔥 Start 60s Run"}
        </button>
      )}

      {/* 🪦 Feb 2026 owner directive — removed "Owner: End Special Event"
         button. Admin can no longer toggle Jewel Hunt from the UI. */}
    </div>
  );
}
